<script setup lang="ts">
    import { ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount } from 'vue'
    import { useId, useRoute } from '@common/composables'
    import { useSurveyPage } from '@RankHR/Regions/pages-core'

    const questionsContainerTop = ref(null)

    const ticksToSave = 5
    let tickHandle = null
    let tick = ref(ticksToSave)

    let input = ref({
        QuestionsGrouping: null,
        SelectedPersonEmail: null,
        SelectedCriterionPath: null,
    })

    const id = useId()

    const { usePersonVotedCommand, useSurveyByPersonQuery } = useSurveyPage()

    const { invokePersonVotedCommandV1, succeed, invoking, command } = usePersonVotedCommand(id)

    const { modelSurveyByPersonQueryV1, runSurveyByPersonQueryV1, authorized, redirectUrl } = useSurveyByPersonQuery(id)

    const model = computed(() => modelSurveyByPersonQueryV1.value)
    const personDisplayName = computed(() => model.value?.PersonDisplayName)

    const allQuestions = computed(() => (model.value?.Questions || [])
        .map(q => ref({ ...q, ...{ InputValue: q.Value, InputComment: q.Comment } }).value))

    const peopleEmails = computed(() => model.value?.PeopleEmails || [])
    const peopleDisplayNames = computed(() => model.value?.PeopleDisplayNames || [])
    const peopleCriteriaNames = computed(() => model.value?.PeopleCriteriaNames || [])
    const criteriaPaths = computed(() => model.value?.CriteriaPaths || [])

    const groupByPerson = computed(() => input.value.QuestionsGrouping == 'ByPerson')
    const groupByCriterion = computed(() => input.value.QuestionsGrouping == 'ByCriterion')
    const groupByMissingComments = computed(() => input.value.QuestionsGrouping == 'ByMissingComments')

    const questions = computed(() => {
        if (groupByPerson.value && input.value.SelectedPersonEmail) {
            return allQuestions.value.filter(a => a.PersonEmail == input.value.SelectedPersonEmail)
        }
        else if (groupByCriterion.value && input.value.SelectedCriterionPath) {
            return allQuestions.value.filter(a => a.CriterionPath == input.value.SelectedCriterionPath)
        }
        else if (groupByMissingComments.value && questionsWithCommentMissing.value.length) {
            return questionsWithCommentMissing.value
        }
        return []
    })

    const questionsTitle = computed(() => {
        if (groupByPerson.value && input.value.SelectedPersonEmail) {
            const idx = peopleEmails.value.indexOf(input.value.SelectedPersonEmail);
            const displayName = peopleDisplayNames.value[idx];
            const cirteriaName = peopleCriteriaNames.value[idx];
            return `Score ${displayName} (${cirteriaName}) in the context of criteria below:`;
        }
        else if (groupByCriterion && input.value.SelectedCriterionPath) {
            return `Score people below in the context of ${input.value.SelectedCriterionPath}:`;
        }
        else if (groupByMissingComments && questionsWithCommentMissing.value.length) {
            return `Please add your comments where it is required, but missing:`;
        }
        return "No questions to display";
    })

    const changedQuestions = computed(() => allQuestions.value.filter(a => a.Value != a.InputValue || a.Comment != a.InputComment))

    const questionsWithCommentRequired = computed(() => changedQuestions.value.filter(a => isCommentRequired(a)))

    const questionsWithCommentMissing = computed(() => allQuestions.value.filter(a => isCommentMissing(a)))

    const progress = computed(() => allQuestions.value.filter(a => a.Value != null || a.InputValue != null).length)

    const progressPercent = computed(() => Math.round(progress.value / allQuestions.value.length * 100))

    const saveBtnTitle = computed(() => `*Save (${changedQuestions.value.length})`)

    watch(authorized, () => {
        if (!authorized.value) {
            location.href = redirectUrl.value
        }
    })

    watch(model, () => {
        input.value.QuestionsGrouping = 'ByPerson';
        document.title = `Survey for ${personDisplayName.value}`;
    })

    watch(groupByPerson, (value) => {
        if (value) {
            input.value.SelectedPersonEmail = peopleEmails.value[0];
        }
        else {
            input.value.SelectedPersonEmail = null;
        }
    })

    watch(groupByCriterion, (value) => {
        if (value) {
            input.value.SelectedCriterionPath = criteriaPaths.value[0];
        }
        else {
            input.value.SelectedCriterionPath = null;
        }
    })

    watch(succeed, () => {
        if (succeed.value) {
            if (command.value.Votes) {
                for (let v of command.value.Votes) {
                    const q = allQuestions.value.find(a => a.CriterionPath == v.CriterionPath && a.PersonId == v.PersonId);

                    if (q) {
                        q.Value = v.Value;
                        q.Comment = v.Comment;
                    }
                }
            }
        }
    })

    onMounted(() => {
        runSurveyByPersonQueryV1()

        tickHandle = setInterval(() => {
            if (tick.value == 0) {
                tick.value = ticksToSave;
                save();
            }
            else {
                tick.value--;
            }
        }, 1000);
    })

    onBeforeMount(() => {
        window.addEventListener("beforeunload", ensureSavedOnExit)
    })

    onBeforeUnmount(() => {
        window.removeEventListener("beforeunload", ensureSavedOnExit);

        if (tickHandle) {
            clearInterval(tickHandle);
        }
    })

    function scrollToTop() {
        if (questionsContainerTop.value) {
            questionsContainerTop.value.scrollIntoView({ behavior: 'smooth' });
        }
    }

    function save() {
        if (!invoking.value && changedQuestions.value.length > 0) {
            invokePersonVotedCommandV1({
                Votes: changedQuestions.value.map(q => ({
                    CriterionPath: q.CriterionPath,
                    VotingCycle: q.VotingCycle,
                    PersonId: q.PersonId,
                    Value: q.InputValue,
                    Comment: q.InputComment,
                }))
            })
        }
    }

    function getProgressByPerson(personEmail) {
        const questions = allQuestions.value.filter(a => a.PersonEmail == personEmail);
        const missing = questions.filter(a => a.InputValue === null);
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function getProgressByCriterion(criterionPath) {
        const questions = allQuestions.value.filter(a => a.CriterionPath == criterionPath);
        const missing = questions.filter(a => a.InputValue === null);
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function getProgressByMissingComments() {
        const questions = allQuestions.value
        const missing = questions.filter(a => isCommentMissing(a));
        return Math.round(100 - missing.length / questions.length * 100);
    }

    function isCommentRequired(q) {
        return (q.InputValue == 1 || q.InputValue == 2) && (!(q.InputComment || '').trim());
    }

    function isCommentMissing(q) {
        return (q.InputValue == 1 || q.InputValue == 2) && (!(q.Comment || '').trim());
    }

    function ensureSavedOnExit(event) {
        if (changedQuestions.value.length == 0) return;
        event.preventDefault();
        event.returnValue = "";
    }

    function handleSave() {
        if (questionsWithCommentRequired.value.length > 0) {
            alert('Please provide comments for questions where you entered "Does not meet.." or "Partially meets..."');
            return;
        }
        save();
    }

    function resetTicker() {
        tick.value = ticksToSave;
    }
</script>

<template>
    <div>
        <div class="container-fluid">
            <div class="row">
                <div v-if="model" class="col-lg-12">
                    <div v-if="model.PersonEnabled && model.VotingCycleStarted">
                        <div class="row" style="height: 70px;">
                            <div class="col-sm-9">
                                <div v-if="progressPercent==100 && changedQuestions.length==0" class="alert alert-success" role="alert">
                                    <h2>Congratulations survey is completed! You can just close this window..</h2>
                                </div>

                                <h2 v-else>
                                    <span>Survey {{model.VotingCycle}} for {{personDisplayName}}</span>
                                    <span v-if="progress!=null">- {{progressPercent}}% ({{progress}}/{{allQuestions.length}})</span>

                                    <a class="btn btn-light" href="https://301083.fs1.hubspotusercontent-na1.net/hubfs/301083/MMB%20Training/MMB%20Board%20Member%20Training.pdf" target="_blank">
                                        <span class="fa fa-info"></span>
                                    </a>
                                </h2>
                            </div>

                            <div class="col-sm-3" style="text-align: right;">
                                <div v-show="changedQuestions.length > 0">
                                    <button @click="handleSave" :disabled="invoking" type="button" class="btn btn-warning mb-2" style="margin: 0;">{{saveBtnTitle}}</button>
                                    <br />
                                    <strong v-show="tick == 0 || invoking">Saving...</strong>
                                    <small v-show="tick > 0 && !invoking">Auto-save in {{tick}}s</small>
                                </div>
                            </div>
                        </div>

                        <div class="form-check form-check-inline">
                            <input v-model="input.QuestionsGrouping" class="form-check-input" type="radio" id="inlineRadio1" value="ByPerson">
                            <label class="form-check-label" for="inlineRadio1">By Person</label>
                        </div>

                        <div class="form-check form-check-inline">
                            <input v-model="input.QuestionsGrouping" class="form-check-input" type="radio" id="inlineRadio2" value="ByCriterion">
                            <label class="form-check-label" for="inlineRadio2">By Competency</label>
                        </div>

                        <div v-if="questionsWithCommentMissing.length" class="form-check form-check-inline">
                            <input v-model="input.QuestionsGrouping" class="form-check-input" type="radio" id="inlineRadio3" value="ByMissingComments">
                            <label class="form-check-label" for="inlineRadio3">Missing comments</label>
                        </div>
                        <hr />
                        <br />

                        <div class="row">
                            <div class="col-sm-3"></div>
                            <div class="col-sm-9"><h4>{{questionsTitle}}</h4></div>
                        </div>

                        <div class="row">
                            <div class="col-sm-3">
                                <ul v-if="groupByPerson" class="list-group">
                                    <a @click="input.SelectedPersonEmail=p; scrollToTop()" v-for="(p, i) in peopleEmails" :key="p"
                                       class="list-group-item" :class="{ active: input.SelectedPersonEmail==p }">
                                        {{peopleDisplayNames[i]}}
                                        ({{getProgressByPerson(p)}}%)
                                        <div class="progress" style="height: 2px;">
                                            <div class="progress-bar bg-secondary" role="progressbar" :style="{ 'width': getProgressByPerson(p) + '%' }"></div>
                                        </div>
                                    </a>
                                </ul>

                                <ul v-else-if="groupByCriterion" class="list-group">
                                    <a @click="input.SelectedCriterionPath=c; scrollToTop()" v-for="c in criteriaPaths" :key="c"
                                       class="list-group-item" :class="{ active: input.SelectedCriterionPath==c }">
                                        {{c}}
                                        ({{getProgressByCriterion(c)}}%)
                                        <div class="progress" style="height: 2px;">
                                            <div class="progress-bar bg-secondary" role="progressbar" :style="{ 'width': getProgressByCriterion(c) + '%' }"></div>
                                        </div>
                                    </a>
                                </ul>

                                <ul v-else-if="groupByMissingComments" class="list-group">
                                    <a @click="input.SelectedCriterionPath=c; scrollToTop()"
                                       class="list-group-item" :class="{ active: input.SelectedCriterionPath==c }">
                                        Missing comments
                                        ({{getProgressByMissingComments()}}%)
                                        <div class="progress" style="height: 2px;">
                                            <div class="progress-bar bg-secondary" role="progressbar" :style="{ 'width': getProgressByMissingComments() + '%' }"></div>
                                        </div>
                                    </a>
                                </ul>
                            </div>

                            <div class="col-sm-9" style="height: calc(100vh - 240px); overflow-x: hidden; overflow-y: auto;">
                                <div ref="questionsContainerTop"></div>
                                <div v-for="(q, i) in questions" style="margin: 0 0 30px 0; padding: 10px;" :class="{ 'question-bg1': i%2==1, 'question-bg2': i%2==0 }">
                                    <h4 v-if="groupByPerson">{{i+1}}. {{q.CriterionPath}}</h4>
                                    <h4 v-else-if="groupByCriterion">{{i+1}}. {{q.PersonDisplayName}} ({{q.PersonCriteriaName}})</h4>
                                    <h4 v-else-if="groupByMissingComments">{{i+1}}. {{q.PersonDisplayName}} ({{q.PersonCriteriaName}}) in the context of {{q.CriterionPath}}</h4>

                                    <p>{{q.CriterionDescription}}</p>
                                    <p v-html="q.CriterionDescriptionHtml"></p>

                                    <div v-for="lvl in q.Scale" class="form-check form-check" :key="lvl.Value">
                                        <input v-model="q.InputValue" :id="`${q.CriterionPath}_${q.PersonId}_${lvl.Value}`" :name="`${q.CriterionPath}_${q.PersonId}_${lvl.Value}`" class="form-check-input" type="radio" :value="lvl.Value">
                                        <label class="form-check-label" :for="`${q.CriterionPath}_${q.PersonId}_${lvl.Value}`">
                                            <span>{{lvl.Label}}</span>
                                            <span v-if="lvl.Value==q.InputValue && isCommentRequired(q)" style="color: red;">(comment is required!)</span>
                                        </label>
                                    </div>

                                    <textarea v-model="q.InputComment" @input="resetTicker" class="form-control" rows="2"></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="model.PersonEnabled && !model.VotingCycleStarted" style="margin: 30px;">
                        <h1>Voting is stopped</h1>
                    </div>

                    <div v-else-if="!model.PersonEnabled" style="margin: 30px;">
                        <h1>Not found</h1>
                    </div>
                </div>

                <div v-else class="col-lg-12">
                    <div style="margin: 30px;">
                        <h1>Loading...</h1>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .question-bg1 {
        background-color: lightblue;
    }

    .question-bg2 {
        background-color: lightgoldenrodyellow;
    }
</style>